import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Modal, Switch, Table, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { PermissionCreateModal } from './PermissionCreateModal';
import { PermissionFormModal } from './PermissionFormModal';
import {
  deletePlanFeature,
  getPlanFeatureDetail,
  getPlanFeatures,
  updatePlanFeature,
  type FeatureDetail,
  type PlanFeature
} from './permissionManageData';

const FEATURE_NO_PATTERN = /^[0-9]{21}$/;

function dash(value?: string) {
  return value ? value : '—';
}

export function PermissionManage() {
  const [rows, setRows] = useState<PlanFeature[]>([]);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [draftName, setDraftName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<FeatureDetail | null>(null);
  const [configHash, setConfigHash] = useState('');
  const [switchingNos, setSwitchingNos] = useState<string[]>([]);
  const detailRequest = useRef(0);
  const switchingRef = useRef(new Set<string>());

  const applySearch = (name = draftName) => {
    setDisplayName(name.trim());
    setPageNum(1);
    setReloadKey((value) => value + 1);
  };

  const resetSearch = () => {
    setDraftName('');
    setDisplayName('');
    setPageNum(1);
    setReloadKey((value) => value + 1);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPlanFeatures({ pageNum, pageSize, displayName })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200) {
          message.error(res.message || '权限列表加载失败');
          setRows([]);
          setTotal(0);
          return;
        }
        const records = res.content?.records;
        const list = Array.isArray(records) ? records : [];
        const pageTotal = res.content?.total;
        if (list.length === 0 && pageNum > 1) {
          setPageNum(pageNum - 1);
          return;
        }
        setRows(list);
        setTotal(typeof pageTotal === 'number' ? pageTotal : list.length);
      })
      .catch(() => {
        if (!cancelled) message.error('权限列表加载失败');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pageNum, pageSize, displayName, reloadKey]);

  const openEdit = (row: PlanFeature) => {
    const featureNo = row.featureNo?.trim();
    if (!featureNo) {
      message.warning('缺少功能编码');
      return;
    }
    const ticket = detailRequest.current + 1;
    detailRequest.current = ticket;
    setFormOpen(true);
    setDetail(null);
    setConfigHash('');
    setDetailLoading(true);
    getPlanFeatureDetail(featureNo)
      .then((res) => {
        if (detailRequest.current !== ticket) return;
        if (res.code !== 200 || !res.content?.data) {
          message.error(res.message || '权限详情加载失败');
          setFormOpen(false);
          return;
        }
        setDetail(res.content.data);
        setConfigHash(typeof res.content.configHash === 'string' ? res.content.configHash : '');
      })
      .catch(() => {
        if (detailRequest.current !== ticket) return;
        message.error('权限详情加载失败');
        setFormOpen(false);
      })
      .finally(() => {
        if (detailRequest.current === ticket) setDetailLoading(false);
      });
  };

  const toggleStatus = async (row: PlanFeature, checked: boolean) => {
    const featureNo = row.featureNo?.trim() ?? '';
    if (!FEATURE_NO_PATTERN.test(featureNo)) {
      message.warning('功能编码须为 21 位数字');
      return;
    }
    if (switchingRef.current.has(featureNo)) return;
    switchingRef.current.add(featureNo);
    setSwitchingNos((prev) => [...prev, featureNo]);
    const status = checked ? 'ACTIVE' : 'DISABLED';
    try {
      const detailRes = await getPlanFeatureDetail(featureNo);
      if (detailRes.code !== 200 || !detailRes.content?.data) {
        if (detailRes.code !== -1) message.error(detailRes.message || '权限详情加载失败');
        return;
      }
      const displayName = detailRes.content.data.displayName?.trim() ?? '';
      const hash = typeof detailRes.content.configHash === 'string' ? detailRes.content.configHash : '';
      if (!displayName || displayName.length > 128) {
        message.warning('缺少功能名称，无法修改状态');
        return;
      }
      if (!hash) {
        message.warning('缺少配置校验值，请稍后重试');
        return;
      }
      const res = await updatePlanFeature(featureNo, {
        displayName,
        status,
        expectedConfigHash: hash
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || '修改状态失败');
        return;
      }
      const nextStatus =
        res.content?.data?.status === 'ACTIVE' || res.content?.data?.status === 'DISABLED'
          ? res.content.data.status
          : status;
      setRows((prev) => prev.map((item) => (item.featureNo === featureNo ? { ...item, status: nextStatus } : item)));
      message.success(res.message || (checked ? '已启用' : '已停用'));
    } catch {
      message.error('修改状态失败');
    } finally {
      switchingRef.current.delete(featureNo);
      setSwitchingNos((prev) => prev.filter((item) => item !== featureNo));
    }
  };

  const confirmDelete = (row: PlanFeature) => {
    const featureNo = row.featureNo?.trim() ?? '';
    if (!FEATURE_NO_PATTERN.test(featureNo)) {
      message.warning('功能编码须为 21 位数字');
      return;
    }
    const name = row.displayName?.trim() || row.featureCode?.trim() || featureNo;
    Modal.confirm({
      title: '确认删除',
      content: `确定删除权限「${name}」吗？`,
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      centered: true,
      onOk: async () => {
        const detailRes = await getPlanFeatureDetail(featureNo);
        if (detailRes.code !== 200 || !detailRes.content?.data) {
          if (detailRes.code !== -1) message.error(detailRes.message || '权限详情加载失败');
          return;
        }
        const hash = typeof detailRes.content.configHash === 'string' ? detailRes.content.configHash : '';
        if (!hash) {
          message.warning('缺少配置校验值，请稍后重试');
          return;
        }
        const res = await deletePlanFeature(featureNo, hash);
        if (res.code !== 200) {
          if (res.code !== -1) message.error(res.message || '删除权限失败');
          return;
        }
        message.success(res.message || '已删除');
        setReloadKey((value) => value + 1);
      }
    });
  };

  const columns: ColumnsType<PlanFeature> = [
    {
      title: '序号',
      width: 64,
      render: (_, __, index) => (pageNum - 1) * pageSize + index + 1
    },
    {
      title: '功能编码',
      dataIndex: 'featureNo',
      width: 220,
      render: (value?: string) => <span className='admin-pkg-code-id'>{dash(value)}</span>
    },
    {
      title: '功能标识',
      dataIndex: 'featureCode',
      width: 180,
      render: (value?: string) => <code className='admin-dict-code'>{dash(value)}</code>
    },
    {
      title: '功能名称',
      dataIndex: 'displayName',
      width: 180,
      ellipsis: true,
      render: (value?: string) => <span className='admin-cell-primary'>{dash(value)}</span>
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      align: 'center',
      render: (value: string | undefined, row) => {
        const featureNo = row.featureNo?.trim() ?? '';
        return (
          <Switch
            size='small'
            checked={value === 'ACTIVE'}
            loading={switchingNos.includes(featureNo)}
            onChange={(checked) => toggleStatus(row, checked)}
          />
        );
      }
    },
    {
      title: '操作',
      key: 'featureNo',
      width: 200,
      render: (_, row) => (
        <div className='admin-row-actions'>
          <button
            type='button'
            className='admin-link-btn'
            onClick={() => openEdit(row)}
          >
            编辑
          </button>

          <button
            type='button'
            className={`admin-link-btn admin-link-btn--danger `}
            onClick={() => confirmDelete(row)}
          >
            删除
          </button>
        </div>
      )
    }
  ];

  return (
    <div className='admin-page-card'>
      <div className='admin-filter-bar admin-pkg-filter-bar'>
        <Input
          allowClear
          className='admin-filter-search'
          placeholder='请输入功能名称'
          value={draftName}
          maxLength={128}
          onChange={(e) => setDraftName(e.target.value)}
          onPressEnter={() => applySearch()}
        />
        <Button type='primary' className='admin-pkg-query-btn' onClick={() => applySearch()}>
          查询
        </Button>
        <Button onClick={resetSearch}>重置</Button>
      </div>
      <div className='mb-3'>
        <Button
          type='primary'
          icon={<PlusOutlined />}
          className='admin-pkg-create-btn'
          onClick={() => setCreateOpen(true)}
        >
          新增
        </Button>
      </div>
      <Table<PlanFeature>
        rowKey={(row, index) => row.featureNo || `${row.featureCode ?? ''}-${index}`}
        columns={columns}
        dataSource={rows}
        loading={loading}
        size='middle'
        className='admin-user-table'
        scroll={{ x: 900 }}
        pagination={{
          current: pageNum,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (count) => `共 ${count} 条`,
          onChange: (current, size) => {
            const nextSize = Math.min(100, Math.max(1, size));
            if (nextSize !== pageSize) {
              setPageNum(1);
              setPageSize(nextSize);
              return;
            }
            setPageNum(current);
          }
        }}
      />
      <PermissionCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          if (pageNum !== 1) setPageNum(1);
          setReloadKey((value) => value + 1);
        }}
      />
      <PermissionFormModal
        open={formOpen}
        loading={detailLoading}
        detail={detail}
        configHash={configHash}
        onClose={() => {
          detailRequest.current += 1;
          setFormOpen(false);
          setDetail(null);
          setConfigHash('');
          setDetailLoading(false);
        }}
        onSaved={() => setReloadKey((value) => value + 1)}
      />
    </div>
  );
}
